import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TitleContext from '../../TitleContext'
import ResponsiveFab from '../Buttons/ResponsiveFab'
import AddIcon from '@mui/icons-material/Add'
import { addIconFudgeFactor } from '../../utils/addIconFudgeFactor'
import { standardSort } from '../../utils/standardSort'
import {
  EventEventTypeEnum,
  FetchEventsResponse,
  PaginationResponse,
  OrderByDirection,
} from '../../swagger'
import { events as eventApi } from '../../api/swagger'
import spacetime, { Spacetime } from 'spacetime'
import { Can } from '@casl/react'
import { useAuth } from '../Routes/AuthProvider'
import Box from '@mui/material/Box'
import { dateToDashString } from '../../utils/dateUtility'
import { reinterpretYearMonthDayAsLocalTime } from '../../utils/reinterpretYearMonthDayAsLocalTime'
import { useSnackbarOnNavigation } from '../../hooks/useSnackbarOnNavigation'
import EventsTab from './EventsTab'
import { useMountEffect } from '../../hooks/useMountEffect'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { defaultPageSize } from '../Pagination/TableFooterPagination'
import { EventSortBy } from './EventEnums'
import { DEFAULT_PAGE_SIZE } from '../../utils/constants'
import { useEventContext } from '../Context/EventContext'
import getOrderByClause from '../../utils/getOrderByClause'
import useEndpoint from '../../hooks/useEndpoint'
import { useSnackbarContext } from '../Context/SnackbarContext'

const EventTypes = {
  AllEvents: 'All Event Types',
  ...EventEventTypeEnum,
}

export const createSpacetime = (
  event: FetchEventsResponse,
  useStartTime = true
): Spacetime => {
  // [year, month, date]
  const dateArray = [
    event.startDate.getUTCFullYear(),
    event.startDate.getUTCMonth(),
    event.startDate.getUTCDate(),
  ]
  switch (useStartTime) {
    case true:
      return spacetime(dateArray, event.timezone).time(event.startTime)
    default:
      return spacetime(dateArray, event.timezone).time(
        event.endTime ?? new Date().toISOString()
      )
  }
}

export const sortEventsByDate = (
  a: FetchEventsResponse,
  b: FetchEventsResponse
): number => {
  const epochA = createSpacetime(a).epoch
  const epochB = createSpacetime(b).epoch

  return standardSort(epochA, epochB)
}

export const getTodaysEarliestEvent = (
  array: FetchEventsResponse[]
): FetchEventsResponse => {
  return array
    .filter((event) => {
      const eventStartDateTime = createSpacetime(event)
      return spacetime.now().startOf('day').isBefore(eventStartDateTime)
    })
    .sort((a, b) => {
      return sortEventsByDate(a, b)
    })[0]
}

const defaultSortProperty = EventSortBy.StartDate
const defaultSortOrder = OrderByDirection.Asc

const eventDefaultCreatorOption = 'All Event Creators'

export interface EventFilter {
  fromDate: string
  page: number
  pageSize: number
  orderBy: Array<string>
  search: string
  eventType: string
  eventCreator: string
}

export const UserEvents: React.FunctionComponent = () => {
  const { setSnackbarState } = useSnackbarContext()
  const { t } = useTranslation()
  const title = t('Events.Title', 'Events')
  const { permissionAbility } = useAuth()
  useSnackbarOnNavigation()
  const { resetContextToDefaults } = useEventContext()
  resetContextToDefaults()
  const { useTitleEffect } = React.useContext(TitleContext)
  useTitleEffect(title)

  const {
    Events: { fetchEvents: fetchEventsLoadingId },
  } = useLoadingIds()

  const [isLoading, setIsLoading] = useState(true)

  const [events, setEvents] = useState<FetchEventsResponse[]>([])

  const [pagination, setPagination] = useState<PaginationResponse>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    orderBy: [{ [defaultSortProperty]: defaultSortOrder }],
  })

  const [minDateFilter, setMinDateFilter] = useState(String)
  const [eventFilters, setEventFilters] = useState<EventFilter>({
    fromDate: dateToDashString(new Date()),
    page: 1,
    pageSize: defaultPageSize,
    orderBy: [`${defaultSortProperty} ${defaultSortOrder}`],
    search: '',
    eventType: EventTypes.AllEvents,
    eventCreator: eventDefaultCreatorOption,
  })

  const initMinDateFilter = () => {
    const today = new Date()
    const minDate = new Date()

    minDate.setDate(1)
    minDate.setMonth(0)
    minDate.setFullYear(today.getFullYear() - 2)

    const minDateFormatted = dateToDashString(
      reinterpretYearMonthDayAsLocalTime(minDate)
    )
    setMinDateFilter(minDateFormatted)
  }

  const { triggerFetch } = useEndpoint<
    void,
    [
      {
        page?: number
        pageSize?: number
        orderBy?: Array<{ [key: string]: OrderByDirection | undefined }>
        eventFilters?: EventFilter
      }?
    ]
  >({
    loadingId: fetchEventsLoadingId,
    swaggerCall: async (abortController, overrideParams) => {
      const currentFilters = overrideParams?.eventFilters ?? eventFilters
      const currentPage = overrideParams?.page ?? pagination.page
      const currentOrderBy = overrideParams?.orderBy ?? pagination.orderBy
      const currentPageSize = overrideParams?.pageSize ?? pagination.pageSize

      const { events, pagination: paginationResponse } =
        await eventApi.fetchEvents({
          ...currentFilters,
          orderBy: getOrderByClause(currentOrderBy),
          page: currentPage,
          pageSize: currentPageSize,
          search:
            currentFilters.search === '' ? undefined : currentFilters.search,
          eventType:
            currentFilters.eventType === EventTypes.AllEvents
              ? undefined
              : currentFilters.eventType,
          eventCreator:
            currentFilters.eventCreator === eventDefaultCreatorOption
              ? undefined
              : currentFilters.eventCreator,
        })
      setEvents(events)
      setIsLoading(false)
      // init the min and max date of datePicker
      initMinDateFilter()

      if (events) {
        setEvents(events)
      }
      if (paginationResponse) {
        const { page, pageSize, totalCount } = paginationResponse

        setPagination({
          ...pagination,
          page,
          pageSize,
          totalCount,
        })
      }
    },
  })

  const newFilter = useRef<EventFilter>({ ...eventFilters })
  const handleEventFilterChange = useCallback(
    <K extends keyof EventFilter>(filter: K, value: EventFilter[K]) => {
      newFilter.current[filter] = value
      const nextFilters = { ...newFilter.current }
      setEventFilters(nextFilters)
      triggerFetch({ eventFilters: nextFilters })
    },
    [newFilter, triggerFetch]
  )

  useMountEffect(() => triggerFetch())

  const handleEventsPagination = (pageInformation: PaginationResponse) => {
    if (pagination.page < 1) return

    // The backend expects eventTypeKey, not eventType
    const orderByKeys = Object.keys(pageInformation.orderBy[0])
    if (orderByKeys.includes('eventType')) {
      pageInformation.orderBy = [
        { eventTypeKey: pageInformation.orderBy[0]['eventType'] },
      ]
    }

    const nextPagination = {
      ...pageInformation,
      orderBy: pageInformation.orderBy,
      page: pageInformation.page + 1,
    }

    setPagination((prev) => ({ ...prev, ...nextPagination }))
    triggerFetch(nextPagination)
  }

  const handleClose = () => {
    setSnackbarState(false)
  }

  const AddEventFAB = () => (
    <Can I="create" on="Event" ability={permissionAbility}>
      <Box component="span" onClick={handleClose}>
        <ResponsiveFab
          to="/events/add-event"
          icon={AddIcon}
          spacingFudgeFactor={addIconFudgeFactor}
          iconLabel={t('Events.AddEventButton.IconLabel', 'Add')}
          textLabel={t('Events.AddEventButton.TextLabel', 'Event')}
          fullLabel={t('Events.AddEventButton.FullLabel', 'Add Event')}
        />
      </Box>
    </Can>
  )

  return (
    <>
      {AddEventFAB()}
      <EventsTab
        events={events}
        isLoading={isLoading}
        minDateFilter={minDateFilter}
        dateToFilter={eventFilters.fromDate}
        handleEventFilterChange={handleEventFilterChange}
        handlePaginationChange={handleEventsPagination}
        pagination={pagination}
      />
    </>
  )
}
export default UserEvents
